import { Injectable } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MemoizedSelector, Action, ActionCreator } from '@ngrx/store';
import { CacheKeyConstants, isNullOrUndefined } from '@uzurv/core';
import { ConfirmationDialogComponent } from '../../components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { Observable } from 'rxjs';

export interface Tab<T, K extends keyof T = any> {
  /**
   * Name of the Tab to be displayed, the component is allowed to change this
   */
  name: string;
  /**
   * Component to be used for the tab
   *
   *  !!! NOTE - This field is automatically set !!!
   */
  component?: string;
  /**
   * Route to be used for the tab
   *
   * !!! NOTE - This field is automatically set !!!
   */
  route?: string;
  /**
   * Material Icon to be used for the tab
   */
  matIcon?: string;
  /**
   * SVG Icon to be used for the tab
   */
  svgIcon?: string;
  /**
   * Route params to be used for the tab
   *
   * ***NOTE*** - This field is automatically set
   */
  routeParams?: { [key: string]: string };
  /**
   * Full route used for snapshot key
   *
   * ***NOTE*** - This field is automatically set
   */
  fullRoute?: string;
  /**
   * Navigation extras are the query params and other navigation options
   *
   * ***NOTE*** - This field is automatically set
   */
  navigationExtras?: NavigationExtras;
  /**
   * If true, the tab will not be closable
   */
  disableClose?: boolean;
  /**
   * TTL in seconds for the tab
   * If the TTL is expired, the tab will be rehydrated
   */
  ttl?: number;
  /**
   * Use a URL param as the tab name
   * Example: /rides/:rideId, tab will use the rideId as the tab name (res-123456)
   */
  useParamAsName?: string;
  /**
   * If true, the tab will show a warning when closed every time
   */
  closeWarning?: boolean;
  /**
   * If true, the tab will show a warning when closed if the selector is true
   * This will override the closeWarning
   */
  closeWarningSelector?: MemoizedSelector<T, boolean>;
  /**
   * Set the selector state to be used for the tab
   * If the selector is not set, State will not be managed)
   */
  selectorState?: MemoizedSelector<T, T>;
  /**
   * If true, when tab is loaded from cache, the initializeAction will be called
   * only when the tab is opened for the first time
   */
  initializeActionOnCacheLoaded?: boolean; // if loaded from cache, initializeAction will be called on first open
  /**
   * TabData, contains tab metadata that aids in tabbing logic
   *
   * ***NOTE*** - This field is automatically set
   */
  tabData?: {
    // Indicates if Tab was loaded from cache when page loaded
    loadedFromCache?: boolean;
    // If tab is setup with initializeAction, this will mark if this has completed.
    initializeActionDispatched?: boolean;
  };

  // Query Param Configuration
  /**
   *
   * QueryParamSync
   * If configured, the tab will manage the a URL to State sync while the tab is active
   *
   */
  queryParamConfig?: {
    queryParamStateKey: K; // Key within the state that will store the params
    queryParamsAction: ActionCreator<string, (props: { state: Pick<T, K> }) => { state: Pick<T, K> } & object>; // Action that allows tabbing to update the target state[queryParamStateKey]
    queryParamDefaults?: Partial<T[K]>; // QueryParam defaults, if no query params in the url, these defaults will be loaded
  };

  /**
   *
   * @param tab - Tab<T> - T is the state of the tab
   * @returns Action to be dispatched
   * Called when the tab is first opened
   */
  initializeAction?: (tab: Tab<T>) => Action; // Called first open
  /**
   *
   * @param tab - Tab<T> - T is the state of the tab
   * @returns Action to be dispatched
   * Activated Action called when the tab is activated
   */
  activatedAction?: (tab: Tab<T>) => Action; // Called on tab activation
  /**
   *
   * @param tab - Tab<T> - T is the state of the tab
   * @returns Action to be dispatched
   * Restore Action called when snapshot is available
   */
  restoreSnapshotAction?: (snapshot: T) => Action; // If snapshot is available
  /**
   *
   * @param tab - Tab<T> - T is the state of the tab
   * @returns Action to be dispatched
   * Action called when no snapshot is available
   */
  noSnapshotAction?: (tab: Tab<T>) => Action; // If no snapshot is available
  /**
   *
   * @param tab - Tab<T> - T is the state of the tab
   * @returns Action to be dispatched
   * Rehydrate Action called when TTL has expired
   */
  rehydrateStateAction?: (tab: Tab<T>) => Action; // If TTL is expired
  /**
   *
   * @returns Action to be dispatched
   * Close Action called on Close Event
   */
  closeTabAction?: () => Action; // Called on close
}

export interface TabSnapshot<T> {
  tabId: string;
  state: T;
  snapshotDateTime: number;
}

@Injectable({ providedIn: 'root' })
export class TabService {
  constructor(private dialog: MatDialog) {}

  /**
   *
   * @param tab
   * @param snapshotDateTime
   * @returns
   */
  hasSnapshotExpired(tab: Tab<any>, snapshotDateTime: number): boolean {
    if (tab.ttl) {
      return (new Date().getTime() - snapshotDateTime) / 1000 > tab.ttl / 1000;
    }
    return false;
  }

  getInitialTabs(): Array<Tab<any>> {
    let tabs: Array<Tab<any>> = this.getTabCache();

    // Ensure tabs is initialized if the cache is empty
    if (isNullOrUndefined(tabs)) {
      tabs = [];
    }

    // Add Home Tab
    if (!tabs.some(tabConfig => tabConfig.route === '/')) {
      tabs.push(this.homeTab());
    }

    return tabs;
  }

  setTabsCache(tabs: Array<Tab<any>>): void {
    sessionStorage.setItem(CacheKeyConstants.OPEN_TABS, JSON.stringify(tabs));
  }

  clearTabsCache(): void {
    sessionStorage.removeItem(CacheKeyConstants.OPEN_TABS);
  }

  tabConfirmationCloseDialog(tab: Tab<any>): Observable<boolean> {
    return this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Are you sure?',
          message: 'You will lose all progress'
        }
      })
      .afterClosed();
  }

  private homeTab(): Tab<null> {
    return {
      name: 'Home',
      route: '/',
      svgIcon: 'uz_home',
      disableClose: true
    };
  }

  private getTabCache(): Array<Tab<any>> {
    const sessionTabs = sessionStorage.getItem(CacheKeyConstants.OPEN_TABS);
    return sessionTabs ? JSON.parse(sessionTabs) : null;
  }
}

export class TabUtils {
  static flattenParams(obj: any, prefix = ''): Record<string, string> {
    const result: Record<string, string> = {};
    Object.entries(obj).forEach(([key, value]) => {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (Array.isArray(value)) {
        if (value.length > 0) {
          result[fullKey] = JSON.stringify(value);
        }
      } else if (typeof value === 'object' && value !== null) {
        const nested = this.flattenParams(value, fullKey);
        Object.assign(result, nested);
      } else if (value !== null && value !== undefined && value !== '') {
        result[fullKey] = String(value);
      }
    });
    return result;
  }

  static unflattenParams(params: Record<string, string>): Record<string, any> {
    const result: Record<string, any> = {};
    Object.entries(params).forEach(([flatKey, value]) => {
      const keys = flatKey.split('.');
      let target = result;
      keys.forEach((key, index) => {
        const isLastKey = index === keys.length - 1;
        if (isLastKey) {
          try {
            target[key] = JSON.parse(value);
          } catch {
            target[key] = value;
          }
        } else {
          target[key] ??= {};
          target = target[key];
        }
      });
    });
    return result;
  }

  static removeDefaultParams(current: Record<string, any>, defaults: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    for (const key in current) {
      const val = current[key];
      const def = defaults[key];
      const isEqual =
        JSON.stringify(val) === JSON.stringify(def) || (Array.isArray(val) && Array.isArray(def) && val.length === 0 && def.length === 0);

      if (!isEqual) {
        result[key] = val;
      }
    }
    return result;
  }
}
